import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useAuth } from '../../../modules/auth';
import { useAlarmedBoxes } from '../../sensors-v2/hooks/useAlarmedBoxes';
import { PageCard, SectionLabel, StatBlock, TOKENS } from '../../../components/design-system';
import { maxSeverity } from '../../../components/box-status-card/severity';

const API_URL = process.env.REACT_APP_API_URL;

// Fetches the fleet-wide box count via the existing filter endpoint
// (read-only, just pagination total). Kept in this component so the
// hero is the only place that needs it.
function useFleetTotal(company_id: number | undefined) {
  return useQuery(
    ['dashboard', 'fleet-total', company_id],
    async () => {
      if (!company_id) return 0;
      const { data } = await axios.get<{ payload?: { pagination?: { total?: number } } }>(
        `${API_URL}/measuring-box/filter`,
        { params: { company_id, take: 0, skip: 1 } },
      );
      return data?.payload?.pagination?.total ?? 0;
    },
    { enabled: !!company_id, refetchInterval: 60_000, refetchOnWindowFocus: false },
  );
}

export const FleetHealthHero: FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : undefined;

  const { data: alarmedData } = useAlarmedBoxes({
    sort: 'gravity_desc', page: 1, items_per_page: 200,
    company_id: companyId,
  });
  const { data: total = 0 } = useFleetTotal(companyId);

  const { high, medium, ok } = useMemo(() => {
    const boxes = alarmedData?.data ?? [];
    let h = 0;
    let m = 0;
    for (const b of boxes) {
      if (!b.active_alarms.length) continue;
      const sev = maxSeverity(b.active_alarms);
      if (sev === 'high') h += 1;
      else if (sev === 'medium') m += 1;
    }
    return { high: h, medium: m, ok: Math.max(0, total - h - m) };
  }, [alarmedData?.data, total]);

  return (
    <PageCard variant="hero">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: TOKENS.spacing.md,
      }}>
        <SectionLabel>Status das caixas</SectionLabel>
        <span
          role="button"
          onClick={() => navigate('/sensors-v2')}
          style={{
            color: TOKENS.color.action,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Ver sensores →
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: TOKENS.spacing.md }}>
        <StatBlock value={high} label="Alta" tone="high" onClick={() => navigate('/sensors-v2')} />
        <StatBlock value={medium} label="Média" tone="medium" onClick={() => navigate('/sensors-v2')} />
        <StatBlock value={ok} label="OK" tone="low" />
      </div>
    </PageCard>
  );
};
